// Localização: frontend/src/pages/Escalas/EscalaPlantao.jsx
// Página principal de gerenciamento de escalas de plantão

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
    Calendar, 
    Sparkles, 
    CheckCircle, 
    XCircle, 
    Clock, 
    Users,
    AlertCircle,
    Plus,
    Filter,
    Settings,
    Trash2,
    Download,
    FileText
} from 'lucide-react';
import moment from 'moment';
import 'moment/locale/pt-br';
import LoadingIndicator from '../../components/Common/LoadingIndicator';
import Alert from '../../components/UI/Alert';
import Modal from '../../components/UI/Modal';
import PrimaryButton from '../../components/Common/PrimaryButton';
import SecondaryButton from '../../components/Common/SecondaryButton';
import { useNavigate } from 'react-router-dom';
import { listarEscalas, sugerirEscala, listarPreferencias, obterEscala, excluirPreferencias } from '../../services/escalaService';
import SugestaoEscala from './SugestaoEscala';
import PreferenciasProfissional from './PreferenciasProfissional';
import { exportarEscala } from '../../utils/escalaExportUtils';
import api from '../../api/api';

moment.locale('pt-br');

const EscalaPlantao = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [escalas, setEscalas] = useState([]);
    const [alertMessage, setAlertMessage] = useState(null);
    
    // Estados para geração de sugestão
    const [showSugestaoModal, setShowSugestaoModal] = useState(false);
    const [periodoInicio, setPeriodoInicio] = useState(moment().startOf('month').format('YYYY-MM-DD'));
    const [periodoFim, setPeriodoFim] = useState(moment().endOf('month').format('YYYY-MM-DD'));
    const [sugestaoGerada, setSugestaoGerada] = useState(null);
    const [gerandoSugestao, setGerandoSugestao] = useState(false);
    
    // Estados para opções de filtro
    const [pacientes, setPacientes] = useState([]);
    const [profissionais, setProfissionais] = useState([]);
    const [pacientesSelecionados, setPacientesSelecionados] = useState([]);
    const [profissionaisSelecionados, setProfissionaisSelecionados] = useState([]);
    const [duracaoTurno, setDuracaoTurno] = useState('');
    const [horaInicioTurno, setHoraInicioTurno] = useState('');
    const [horaFimTurno, setHoraFimTurno] = useState('');
    
    // Estados para modal de preferências
    const [showPreferenciasModal, setShowPreferenciasModal] = useState(false);
    const [profissionalSelecionado, setProfissionalSelecionado] = useState(null);
    
    // Estados para visualização de preferências
    const [preferencias, setPreferencias] = useState([]);
    const [loadingPreferencias, setLoadingPreferencias] = useState(false);
    const [showPreferenciasList, setShowPreferenciasList] = useState(false);
    const [preferenciaParaExcluir, setPreferenciaParaExcluir] = useState(null);
    const [excluindoPreferencia, setExcluindoPreferencia] = useState(false);

    // Filtros
    const [filtroStatus, setFiltroStatus] = useState('');
    const [filtroPeriodoInicio, setFiltroPeriodoInicio] = useState('');
    const [filtroPeriodoFim, setFiltroPeriodoFim] = useState('');

    useEffect(() => {
        carregarEscalas();
    }, [filtroStatus, filtroPeriodoInicio, filtroPeriodoFim]);

    // Carregar pacientes e profissionais quando o modal abrir
    useEffect(() => {
        if (showSugestaoModal && !sugestaoGerada) {
            carregarPacientes();
            carregarProfissionais();
        }
    }, [showSugestaoModal, sugestaoGerada]);

    // Carregar profissionais quando o modal de preferências abrir
    useEffect(() => {
        if (showPreferenciasModal) {
            carregarProfissionais();
        }
    }, [showPreferenciasModal]);

    // Carregar preferências quando a lista for exibida
    useEffect(() => {
        if (showPreferenciasList) {
            carregarPreferencias();
        }
    }, [showPreferenciasList]);

    const carregarPacientes = async () => {
        try {
            const response = await api.get('/pacientes');
            // Filtrar apenas pacientes ativos (excluir cancelados e excluídos)
            const pacientesAtivos = (response.data || []).filter(
                paciente => paciente.status === 'Ativo'
            );
            setPacientes(pacientesAtivos);
        } catch (error) {
            console.error('Erro ao carregar pacientes:', error);
        }
    };

    const carregarProfissionais = async () => {
        try {
            const response = await api.get('/usuarios');
            // Filtrar apenas profissionais (roles que podem ter plantões)
            const profs = (response.data || []).filter(u => 
                ['enfermeiro', 'medico', 'fisioterapeuta', 'cuidador', 'profissional'].includes(
                    u.role?.toLowerCase()
                )
            );
            setProfissionais(profs);
        } catch (error) {
            console.error('Erro ao carregar profissionais:', error);
        }
    };

    const carregarPreferencias = async () => {
        setLoadingPreferencias(true);
        try {
            const response = await listarPreferencias();
            setPreferencias(response.data || response || []);
        } catch (error) {
            console.error('Erro ao carregar preferências:', error);
            setAlertMessage({
                type: 'error',
                message: 'Erro ao carregar preferências. Tente novamente.'
            });
        } finally {
            setLoadingPreferencias(false);
        }
    };

    const handleExcluirPreferencia = async () => {
        if (!preferenciaParaExcluir) return;

        setExcluindoPreferencia(true);
        try {
            await excluirPreferencias(preferenciaParaExcluir.id);
            setAlertMessage({
                type: 'success',
                message: 'Preferência excluída com sucesso.'
            });
            setPreferenciaParaExcluir(null);
            // Recarregar lista de preferências
            await carregarPreferencias();
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Erro ao excluir preferência';
            setAlertMessage({
                type: 'error',
                message: errorMessage
            });
        } finally {
            setExcluindoPreferencia(false);
        }
    };

    const carregarEscalas = async () => {
        setLoading(true);
        try {
            const filtros = {};
            if (filtroStatus) filtros.status = filtroStatus;
            if (filtroPeriodoInicio) filtros.periodo_inicio = filtroPeriodoInicio;
            if (filtroPeriodoFim) filtros.periodo_fim = filtroPeriodoFim;

            const dados = await listarEscalas(filtros);
            setEscalas(dados);
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Erro ao carregar escalas';
            setAlertMessage({ type: 'error', message: errorMessage });
        } finally {
            setLoading(false);
        }
    };

    const handleGerarSugestao = async () => {
        if (!periodoInicio || !periodoFim) {
            setAlertMessage({ 
                type: 'error', 
                message: 'Por favor, selecione o período para gerar a sugestão' 
            });
            return;
        }

        if (moment(periodoInicio).isAfter(moment(periodoFim))) {
            setAlertMessage({ 
                type: 'error', 
                message: 'Data de início deve ser anterior à data de fim' 
            });
            return;
        }

        setGerandoSugestao(true);
        setAlertMessage(null);

        try {
            // Preparar opções
            const opcoes = {};
            
            if (pacientesSelecionados.length > 0) {
                opcoes.pacientes_ids = pacientesSelecionados;
            }
            
            if (profissionaisSelecionados.length > 0) {
                opcoes.profissionais_ids = profissionaisSelecionados;
            }
            
            if (duracaoTurno) {
                opcoes.duracao_turno_horas = parseInt(duracaoTurno);
            }
            
            if (horaInicioTurno && horaFimTurno) {
                // Converter formato HH:MM para HH:MM:SS
                const formatarHora = (hora) => {
                    if (!hora) return null;
                    // Se já está no formato HH:MM:SS, retornar como está
                    if (hora.length === 8) return hora;
                    // Se está no formato HH:MM, adicionar :00
                    if (hora.length === 5) return `${hora}:00`;
                    return hora;
                };
                
                opcoes.horario_turno = {
                    inicio: formatarHora(horaInicioTurno),
                    fim: formatarHora(horaFimTurno)
                };
            }

            const resultado = await sugerirEscala(periodoInicio, periodoFim, opcoes);
            setSugestaoGerada(resultado.data);
            setShowSugestaoModal(true);
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Erro ao gerar sugestão de escala';
            setAlertMessage({ type: 'error', message: errorMessage });
        } finally {
            setGerandoSugestao(false);
        }
    };

    const handleSugestaoAprovada = () => {
        setShowSugestaoModal(false);
        setSugestaoGerada(null);
        carregarEscalas();
        setAlertMessage({ 
            type: 'success', 
            message: 'Escala aprovada com sucesso!' 
        });
    };

    const handleSugestaoRejeitada = () => {
        setShowSugestaoModal(false);
        setSugestaoGerada(null);
        carregarEscalas();
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'rascunho': { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Rascunho' },
            'sugerida': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Sugerida' },
            'aprovada': { bg: 'bg-green-100', text: 'text-green-800', label: 'Aprovada' },
            'ativa': { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Ativa' },
            'finalizada': { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Finalizada' }
        };

        const config = statusConfig[status] || statusConfig['rascunho'];
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
                {config.label}
            </span>
        );
    };

    const getOrigemBadge = (origem) => {
        if (origem === 'ia_sugerida' || origem === 'ia_aprovada') {
            return (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    IA
                </span>
            );
        }
        return (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                Manual
            </span>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
                                <Calendar className="w-8 h-8 text-primary-600" />
                                Escalas de Plantão
                            </h1>
                            <p className="text-gray-600 mt-1">
                                Gerencie e crie escalas de plantão com suporte de IA
                            </p>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <SecondaryButton
                                onClick={() => {
                                    setShowPreferenciasList(!showPreferenciasList);
                                    if (!showPreferenciasList) {
                                        carregarPreferencias();
                                    }
                                }}
                                className="flex items-center gap-2"
                            >
                                <Users className="w-4 h-4" />
                                {showPreferenciasList ? 'Ocultar' : 'Ver'} Preferências
                            </SecondaryButton>
                            <SecondaryButton
                                onClick={() => {
                                    setProfissionalSelecionado(null);
                                    setShowPreferenciasModal(true);
                                }}
                                className="flex items-center gap-2"
                            >
                                <Settings className="w-4 h-4" />
                                Configurar Preferências
                            </SecondaryButton>
                            <SecondaryButton
                                onClick={() => setShowSugestaoModal(true)}
                                className="flex items-center gap-2"
                            >
                                <Sparkles className="w-4 h-4" />
                                Gerar Sugestão com IA
                            </SecondaryButton>
                        </div>
                    </div>
                </div>

                {/* Alertas */}
                {alertMessage && (
                    <div className="mb-4">
                        <Alert
                            type={alertMessage.type}
                            message={alertMessage.message}
                            onClose={() => setAlertMessage(null)}
                        />
                    </div>
                )}

                {/* Filtros */}
                <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Status
                            </label>
                            <select
                                value={filtroStatus}
                                onChange={(e) => setFiltroStatus(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                                <option value="">Todos</option>
                                <option value="rascunho">Rascunho</option>
                                <option value="sugerida">Sugerida</option>
                                <option value="aprovada">Aprovada</option>
                                <option value="ativa">Ativa</option>
                                <option value="finalizada">Finalizada</option>
                            </select>
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Período Início
                            </label>
                            <input
                                type="date"
                                value={filtroPeriodoInicio}
                                onChange={(e) => setFiltroPeriodoInicio(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Período Fim
                            </label>
                            <input
                                type="date"
                                value={filtroPeriodoFim}
                                onChange={(e) => setFiltroPeriodoFim(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                    </div>
                </div>

                {/* Lista de Preferências */}
                {showPreferenciasList && (
                    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                                <Users className="w-5 h-5 text-primary-600" />
                                Preferências Configuradas
                            </h2>
                            <button
                                onClick={() => setShowPreferenciasList(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ✕
                            </button>
                        </div>

                        {loadingPreferencias ? (
                            <LoadingIndicator message="Carregando preferências..." />
                        ) : preferencias.length === 0 ? (
                            <div className="text-center py-8">
                                <Users className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                                <p className="text-gray-600 mb-4">
                                    Nenhuma preferência configurada ainda.
                                </p>
                                <SecondaryButton
                                    onClick={() => {
                                        setShowPreferenciasList(false);
                                        setProfissionalSelecionado(null);
                                        setShowPreferenciasModal(true);
                                    }}
                                >
                                    Configurar Primeira Preferência
                                </SecondaryButton>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {preferencias.map((pref) => {
                                    const diasSemana = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
                                    return (
                                        <div
                                            key={pref.id}
                                            className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <h3 className="text-lg font-semibold text-gray-900">
                                                            {pref.usuario_nome || 'Profissional'}
                                                        </h3>
                                                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                                            {pref.usuario_role || 'Profissional'}
                                                        </span>
                                                        {pref.paciente_id && (
                                                            <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                                                                Específico para Paciente
                                                            </span>
                                                        )}
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
                                                        {/* Carga Horária */}
                                                        <div>
                                                            <p className="text-xs text-gray-500 mb-1">Carga Horária</p>
                                                            <p className="text-sm text-gray-900">
                                                                Semanal: {pref.carga_horaria_semanal_maxima}h | 
                                                                Diária: {pref.carga_horaria_diaria_maxima}h
                                                            </p>
                                                        </div>

                                                        {/* Intervalo de Descanso */}
                                                        <div>
                                                            <p className="text-xs text-gray-500 mb-1">Intervalo Mínimo</p>
                                                            <p className="text-sm text-gray-900">
                                                                {pref.intervalo_descanso_minimo} horas
                                                            </p>
                                                        </div>

                                                        {/* Regras de Turno */}
                                                        {(pref.duracao_turno_horas || pref.turno_obrigatorio || pref.horario_turno_inicio) && (
                                                            <div>
                                                                <p className="text-xs text-gray-500 mb-1">Regras de Turno</p>
                                                                <div className="text-sm text-gray-900">
                                                                    {pref.duracao_turno_horas && (
                                                                        <span className="block">Duração: {pref.duracao_turno_horas}h</span>
                                                                    )}
                                                                    {pref.turno_obrigatorio && (
                                                                        <span className="block capitalize">Tipo: {pref.turno_obrigatorio}</span>
                                                                    )}
                                                                    {pref.horario_turno_inicio && pref.horario_turno_fim && (
                                                                        <span className="block">
                                                                            {pref.horario_turno_inicio.substring(0, 5)} - {pref.horario_turno_fim.substring(0, 5)}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Dias Preferidos */}
                                                        {pref.dias_semana_preferidos && pref.dias_semana_preferidos.length > 0 && (
                                                            <div>
                                                                <p className="text-xs text-gray-500 mb-1">Dias Preferidos</p>
                                                                <p className="text-sm text-gray-900">
                                                                    {pref.dias_semana_preferidos.map(d => diasSemana[d]).join(', ')}
                                                                </p>
                                                            </div>
                                                        )}

                                                        {/* Dias Indisponíveis */}
                                                        {pref.dias_semana_indisponiveis && pref.dias_semana_indisponiveis.length > 0 && (
                                                            <div>
                                                                <p className="text-xs text-gray-500 mb-1">Dias Indisponíveis</p>
                                                                <p className="text-sm text-gray-900">
                                                                    {pref.dias_semana_indisponiveis.map(d => diasSemana[d]).join(', ')}
                                                                </p>
                                                            </div>
                                                        )}

                                                        {/* Tipos de Plantão Preferidos */}
                                                        {pref.preferencia_tipo_plantao && pref.preferencia_tipo_plantao.length > 0 && (
                                                            <div>
                                                                <p className="text-xs text-gray-500 mb-1">Tipos Preferidos</p>
                                                                <p className="text-sm text-gray-900 capitalize">
                                                                    {pref.preferencia_tipo_plantao.join(', ')}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {pref.observacoes && (
                                                        <div className="mt-3 pt-3 border-t border-gray-200">
                                                            <p className="text-xs text-gray-500 mb-1">Observações</p>
                                                            <p className="text-sm text-gray-700">{pref.observacoes}</p>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="ml-4 flex gap-2">
                                                    <button
                                                        onClick={() => {
                                                            const prof = profissionais.find(p => p.id === pref.usuario_id);
                                                            setProfissionalSelecionado(prof || { id: pref.usuario_id, nome_completo: pref.usuario_nome });
                                                            setShowPreferenciasList(false);
                                                            setShowPreferenciasModal(true);
                                                        }}
                                                        className="px-3 py-1 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors"
                                                    >
                                                        Editar
                                                    </button>
                                                    <button
                                                        onClick={() => setPreferenciaParaExcluir(pref)}
                                                        className="px-3 py-1 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors flex items-center gap-1"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                        Excluir
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* Lista de Escalas */}
                {loading ? (
                    <LoadingIndicator message="Carregando escalas..." />
                ) : escalas.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                        <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            Nenhuma escala encontrada
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Comece gerando uma sugestão de escala com IA ou criando uma manualmente.
                        </p>
                        <PrimaryButton
                            onClick={() => setShowSugestaoModal(true)}
                            className="flex items-center gap-2 mx-auto"
                        >
                            <Sparkles className="w-4 h-4" />
                            Gerar Primeira Sugestão
                        </PrimaryButton>
                    </div>
                ) : (
                    <div className="grid gap-4">
                        {escalas.map((escala) => (
                            <div
                                key={escala.id}
                                className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow"
                            >
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <h3 className="text-lg font-semibold text-gray-900">
                                                Escala de {moment(escala.periodo_inicio).format('MMMM [de] YYYY')}
                                            </h3>
                                            {getStatusBadge(escala.status)}
                                            {getOrigemBadge(escala.origem)}
                                        </div>
                                        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                                            <div className="flex items-center gap-1">
                                                <Calendar className="w-4 h-4" />
                                                <span>
                                                    {moment(escala.periodo_inicio).format('DD/MM/YYYY')} - {moment(escala.periodo_fim).format('DD/MM/YYYY')}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Users className="w-4 h-4" />
                                                <span>{escala.total_itens || 0} plantões</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <SecondaryButton
                                            onClick={() => navigate(`/escalas/${escala.id}`)}
                                        >
                                            Ver Detalhes
                                        </SecondaryButton>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Modal de Geração de Sugestão */}
                <Modal
                    isOpen={showSugestaoModal}
                    onClose={() => {
                        setShowSugestaoModal(false);
                        setSugestaoGerada(null);
                        // Resetar opções ao fechar
                        setPacientesSelecionados([]);
                        setProfissionaisSelecionados([]);
                        setDuracaoTurno('');
                        setHoraInicioTurno('');
                        setHoraFimTurno('');
                    }}
                    title={sugestaoGerada ? "Sugestão Gerada" : "Gerar Sugestão de Escala com Higeia IA"}
                    size="xl"
                >
                    {!sugestaoGerada ? (
                        <div className="space-y-4">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <Sparkles className="w-5 h-5 text-blue-600 mt-0.5" />
                                    <div>
                                        <h4 className="font-semibold text-blue-900 mb-1">
                                            Geração Inteligente de Escala
                                        </h4>
                                        <p className="text-sm text-blue-800">
                                            A IA analisará a disponibilidade dos profissionais, preferências, 
                                            agendamentos existentes e gerará uma sugestão otimizada de escala.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Data de Início *
                                    </label>
                                    <input
                                        type="date"
                                        value={periodoInicio}
                                        onChange={(e) => setPeriodoInicio(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Data de Fim *
                                    </label>
                                    <input
                                        type="date"
                                        value={periodoFim}
                                        onChange={(e) => setPeriodoFim(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Opções Avançadas */}
                            <div className="border-t pt-4 mt-4">
                                <h4 className="text-sm font-semibold text-gray-900 mb-3">Opções Avançadas (Opcional)</h4>
                                
                                {/* Seleção de Pacientes */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Pacientes Específicos
                                    </label>
                                    <select
                                        multiple
                                        value={pacientesSelecionados}
                                        onChange={(e) => {
                                            const selected = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                                            setPacientesSelecionados(selected);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[100px]"
                                        size="4"
                                    >
                                        {pacientes.map((paciente) => (
                                            <option key={paciente.id} value={paciente.id}>
                                                {paciente.nome_completo || paciente.nome}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Segure Ctrl/Cmd para selecionar múltiplos. Deixe vazio para todos os pacientes.
                                    </p>
                                </div>

                                {/* Seleção de Profissionais */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Profissionais Específicos
                                    </label>
                                    <select
                                        multiple
                                        value={profissionaisSelecionados}
                                        onChange={(e) => {
                                            const selected = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                                            setProfissionaisSelecionados(selected);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[100px]"
                                        size="4"
                                    >
                                        {profissionais.map((prof) => (
                                            <option key={prof.id} value={prof.id}>
                                                {prof.nome_completo || prof.nome} ({prof.role})
                                            </option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Segure Ctrl/Cmd para selecionar múltiplos. Deixe vazio para todos os profissionais.
                                    </p>
                                </div>

                                {/* Configuração de Turno */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Duração do Turno (horas)
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="24"
                                            value={duracaoTurno}
                                            onChange={(e) => setDuracaoTurno(e.target.value)}
                                            placeholder="Ex: 6, 8, 12"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Hora Início Turno
                                        </label>
                                        <input
                                            type="time"
                                            value={horaInicioTurno}
                                            onChange={(e) => setHoraInicioTurno(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Hora Fim Turno
                                        </label>
                                        <input
                                            type="time"
                                            value={horaFimTurno}
                                            onChange={(e) => setHoraFimTurno(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        />
                                    </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">
                                    Configure turnos específicos. Deixe vazio para usar turnos padrão (manhã, tarde, noite, madrugada).
                                </p>
                            </div>

                            <div className="flex justify-end gap-2 pt-4">
                                <SecondaryButton
                                    onClick={() => {
                                        setShowSugestaoModal(false);
                                        setSugestaoGerada(null);
                                    }}
                                >
                                    Cancelar
                                </SecondaryButton>
                                <PrimaryButton
                                    onClick={handleGerarSugestao}
                                    disabled={gerandoSugestao}
                                    className="flex items-center gap-2"
                                >
                                    {gerandoSugestao ? (
                                        <>
                                            <LoadingIndicator small />
                                            Gerando...
                                        </>
                                    ) : (
                                        <>
                                            <Sparkles className="w-4 h-4" />
                                            Gerar Sugestão
                                        </>
                                    )}
                                </PrimaryButton>
                            </div>
                        </div>
                    ) : (
                        <SugestaoEscala
                            sugestao={sugestaoGerada}
                            onAprovar={handleSugestaoAprovada}
                            onRejeitar={handleSugestaoRejeitada}
                            onClose={() => {
                                setShowSugestaoModal(false);
                                setSugestaoGerada(null);
                            }}
                        />
                    )}
                </Modal>

                {/* Modal de Preferências */}
                <PreferenciasProfissional
                    profissional={profissionalSelecionado}
                    profissionais={profissionais}
                    pacientes={pacientes}
                    isOpen={showPreferenciasModal}
                    onClose={() => {
                        setShowPreferenciasModal(false);
                        setProfissionalSelecionado(null);
                    }}
                    onSave={() => {
                        // Recarregar profissionais e preferências
                        carregarProfissionais();
                        if (showPreferenciasList) {
                            carregarPreferencias();
                        }
                    }}
                />

                {/* Modal de Confirmação de Exclusão de Preferência */}
                {preferenciaParaExcluir && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                            <div className="flex items-start gap-3 mb-4">
                                <AlertCircle className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" />
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        Excluir Preferência
                                    </h3>
                                    <p className="text-sm text-gray-600 mb-3">
                                        Tem certeza que deseja excluir as preferências de <strong>{preferenciaParaExcluir.usuario_nome}</strong>?
                                        {preferenciaParaExcluir.paciente_nome && (
                                            <> (Paciente: <strong>{preferenciaParaExcluir.paciente_nome}</strong>)</>
                                        )}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        Esta ação não pode ser desfeita.
                                    </p>
                                </div>
                            </div>
                            <div className="flex justify-end gap-2">
                                <SecondaryButton
                                    onClick={() => setPreferenciaParaExcluir(null)}
                                    disabled={excluindoPreferencia}
                                >
                                    Cancelar
                                </SecondaryButton>
                                <PrimaryButton
                                    onClick={handleExcluirPreferencia}
                                    disabled={excluindoPreferencia}
                                    className="bg-red-600 hover:bg-red-700"
                                >
                                    {excluindoPreferencia ? (
                                        <>
                                            <LoadingIndicator small />
                                            Excluindo...
                                        </>
                                    ) : (
                                        <>
                                            <Trash2 className="w-4 h-4 mr-2" />
                                            Excluir
                                        </>
                                    )}
                                </PrimaryButton>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default EscalaPlantao;

